import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { authApi } from '@web/core/auth/auth-api';
import { destinationAfterSignIn } from '@web/core/auth/password-change';
import { LoginForm } from '@web/modules/auth/login-form';

export interface LoginSearch {
  /** Where to go after signing in (checked by `safeNextPath`). */
  next?: string | undefined;
  /** Set when a platform-admin session tried to enter the CRM web. */
  reason?: 'platform_operator' | undefined;
}

export const Route = createFileRoute('/_auth/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const next = typeof search['next'] === 'string' ? search['next'] : undefined;
    const reason = search['reason'] === 'platform_operator' ? 'platform_operator' : undefined;
    return { ...(next ? { next } : {}), ...(reason ? { reason } : {}) };
  },
  component: LoginRoute,
});

function LoginRoute() {
  const { next, reason } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const initialError =
    reason === 'platform_operator' ? t('errors.api.PLATFORM_OPERATOR_ON_WEB') : null;

  return (
    <LoginForm
      initialError={initialError}
      onAuthenticated={async (session) => {
        if (session.membership.role === 'platform-admin') {
          queryClient.removeQueries({ queryKey: ['auth'] });
          await authApi.logout().catch(() => undefined);
          await navigate({
            to: '/login',
            search: { reason: 'platform_operator' },
            replace: true,
          });
          return;
        }
        queryClient.removeQueries({ queryKey: ['auth'] });
        await navigate({ href: destinationAfterSignIn(session.user, next), replace: true });
      }}
    />
  );
}
